import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { Card, Select, Row, Col, Spin, Space, Empty, Modal, Table, Tag, Input } from "antd";
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
    CalendarOutlined, BankOutlined, FilterOutlined, AppstoreOutlined,
    UserOutlined, SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;
const { Search } = Input;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

// Comprehensive Leave Categories Standard aligned with backend schema
const LEAVE_CATEGORIES = [
    {
        key: "cl",
        keys: ["total_cl", "cl", "casual", "casual_leave", "total_casual_leave"],
        label: "Casual Leave",
        abbr: "CL",
        color: "#CB00CC",
        bg: "#fdf4ff",
        border: "#f5d0fe",
        isLeaveDay: true,
    },
    {
        key: "sl",
        keys: ["total_sl", "sl", "sick", "sick_leave", "total_sick_leave"],
        label: "Sick Leave",
        abbr: "SL",
        color: "#FF0066",
        bg: "#fff1f2",
        border: "#fecaca",
        isLeaveDay: true,
    },
    {
        key: "co",
        keys: ["total_co", "total_compoff", "compoff", "co", "comp_off"],
        label: "Comp Off",
        abbr: "CO",
        color: "#1FA2CC",
        bg: "#e0f2fe",
        border: "#7dd3fc",
        isLeaveDay: true,
    },
    {
        key: "lop",
        keys: ["total_lop", "total_nlb", "lop", "nlb", "loss_of_pay", "total_loss_of_pay"],
        label: "NLB / LOP",
        abbr: "NLB",
        color: "#bf4406",
        bg: "#fff7ed",
        border: "#fed7aa",
        isLeaveDay: true,
    },
    {
        key: "wo",
        keys: ["total_wo", "total_weekoff", "weekoff", "wo", "week_off"],
        label: "Week Off",
        abbr: "WO",
        color: "#eab308",
        bg: "#fefce8",
        border: "#fef08a",
        isLeaveDay: false,
    },
    {
        key: "perm",
        keys: ["total_permission", "total_permissions", "permissions", "permission"],
        label: "Permissions",
        abbr: "Perm",
        color: "#10b981",
        bg: "#f0fdf4",
        border: "#bbf7d0",
        isLeaveDay: false,
        unit: "logs",
    },
];

function roundVal(v) {
    if (v === null || v === undefined || isNaN(v)) return 0;
    const num = Number(v);
    return num % 1 === 0 ? num : Math.round((num + Number.EPSILON) * 10) / 10;
}

export default function OrganisationLeave({
    organisationId: propOrgId,
    departmentId: propDeptId,
    date: propDate,
}) {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const initialDate = propDate ? dayjs(propDate) : dayjs();
    const [month, setMonth] = useState(initialDate.month() + 1);
    const [year, setYear] = useState(initialDate.year());

    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState(propOrgId || "ALL");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState(propDeptId || "ALL");

    // Drilldown modal state
    const [drillModal, setDrillModal] = useState({ open: false, leaveType: "", title: "", color: "" });
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillEmployees, setDrillEmployees] = useState([]);
    const [drillSearch, setDrillSearch] = useState("");

    // Sync with parent props when they change
    useEffect(() => {
        if (propOrgId !== undefined && propOrgId !== organisationId) {
            setOrganisationId(propOrgId);
        }
    }, [propOrgId]);

    useEffect(() => {
        if (propDeptId !== undefined && propDeptId !== departmentId) {
            setDepartmentId(propDeptId);
        }
    }, [propDeptId]);

    useEffect(() => {
        if (propDate) {
            const d = dayjs(propDate);
            if (d.isValid()) {
                setMonth(d.month() + 1);
                setYear(d.year());
            }
        }
    }, [propDate]);

    // Fetch Organisations
    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data?.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    // Fetch Departments when Organisation changes
    useEffect(() => {
        const q = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        axios.get(`${backUrl}/api/emp/departments/${q}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            setDepartments(res.data?.results || res.data || []);
        }).catch(() => setDepartments([]));
    }, [organisationId, backUrl, authData?.access]);

    // Fetch Organisation Leave Summary Report
    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;
        if (departmentId !== "ALL") params.department_id = departmentId;

        axios.get(`${backUrl}/api/reports/organisation-leave-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            const payload = res.data?.data || res.data?.results || res.data || {};
            setData(payload);
        }).catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, departmentId, month, year, backUrl, authData?.access]);

    // Helper to safely extract category value from backend payload
    const getCategoryValue = (cat) => {
        if (!data || typeof data !== "object") return 0;
        for (const k of cat.keys) {
            if (data[k] !== undefined && data[k] !== null && data[k] !== "") {
                return Number(data[k]) || 0;
            }
        }
        return 0;
    };

    // Chart Data for Donut (Approved Leave Breakdown: CL, SL, EL, CO, NLB)
    const chartData = useMemo(() => {
        if (!data) return [];
        return LEAVE_CATEGORIES
            .filter(cat => cat.isLeaveDay)
            .map(cat => ({
                name: cat.abbr,
                fullName: cat.label,
                value: roundVal(getCategoryValue(cat)),
                color: cat.color,
            }))
            .filter(i => i.value > 0);
    }, [data]);

    // Actual Leave Days Sum (excluding permissions and week-offs)
    const totalApprovedLeaveDays = useMemo(() => {
        return LEAVE_CATEGORIES
            .filter(cat => cat.isLeaveDay)
            .reduce((sum, cat) => sum + getCategoryValue(cat), 0);
    }, [data]);

    // Open Drilldown Modal on category click
    const openDrilldown = async (cat) => {
        const val = roundVal(getCategoryValue(cat));
        if (val === 0) return;

        setDrillModal({
            open: true,
            leaveType: cat.abbr,
            title: `${cat.label} (${cat.abbr}) Breakdown — ${MONTHS[month - 1]} ${year}`,
            color: cat.color,
        });
        setDrillSearch("");
        setDrillLoading(true);
        setDrillEmployees([]);

        try {
            const params = { month, year, leave_type: cat.abbr };
            if (organisationId !== "ALL") params.organisation_id = organisationId;
            if (departmentId !== "ALL") params.department_id = departmentId;

            const res = await axios.get(`${backUrl}/api/reports/organisation-leave-summary/`, {
                params,
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDrillEmployees(res.data?.employees || []);
        } catch (err) {
            console.error("Error fetching leave drilldown:", err);
        } finally {
            setDrillLoading(false);
        }
    };

    const filteredDrillEmployees = useMemo(() => {
        if (!drillSearch.trim()) return drillEmployees;
        const q = drillSearch.toLowerCase();
        return drillEmployees.filter(e =>
            (e.name || "").toLowerCase().includes(q) ||
            (e.emp_code || "").toLowerCase().includes(q) ||
            (e.department || "").toLowerCase().includes(q)
        );
    }, [drillEmployees, drillSearch]);

    return (
        <Card
            variant="borderless"
            style={{
                borderRadius: 16, background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                border: "1px solid #e2e8f0", minHeight: 450,
                position: "relative",
            }}
            styles={{ body: { padding: "18px 20px" } }}
        >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "linear-gradient(135deg, #0e497b 0%, #1e40af 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", boxShadow: "0 2px 8px rgba(14,73,123,0.25)", flexShrink: 0
                    }}>
                        <CalendarOutlined style={{ fontSize: 17 }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
                            Organisation Leave Summary
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                            {MONTHS[month - 1]} {year} &middot; Approved Leave Breakdown &amp; Permissions
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Grid */}
            <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                <Col xs={12} sm={6}>
                    <Select
                        value={organisationId}
                        onChange={(v) => { setOrganisationId(v || "ALL"); setDepartmentId("ALL"); }}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onClear={() => { setOrganisationId("ALL"); setDepartmentId("ALL"); }}
                        placeholder="Organisation"
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        <Option value="ALL">
                            <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>All Orgs ({organisations.length})</span></Space>
                        </Option>
                        {organisations.map(o => (
                            <Option key={o.id} value={o.id}>
                                <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>{o.name}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={departmentId}
                        onChange={(v) => setDepartmentId(v || "ALL")}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onClear={() => setDepartmentId("ALL")}
                        placeholder="Department"
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        <Option value="ALL">
                            <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>All Depts ({departments.length})</span></Space>
                        </Option>
                        {departments.map(d => (
                            <Option key={d.id} value={d.id}>
                                <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>{d.name}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={month}
                        onChange={setMonth}
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        {MONTHS.map((m, i) => (
                            <Option key={i + 1} value={i + 1}>
                                <Space size={4}><CalendarOutlined style={{ color: "#2563eb" }} /> <span>{m}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={year}
                        onChange={setYear}
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        {YEARS.map(y => (
                            <Option key={y} value={y}>
                                <Space size={4}><FilterOutlined style={{ color: "#7c3aed" }} /> <span>{y}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Approved Leave Breakdown...</div>
                </div>
            ) : !data || (chartData.length === 0 && totalApprovedLeaveDays === 0) ? (
                <Empty description="No approved leave records for selected filters" style={{ padding: "60px 0" }} />
            ) : (
                <>
                    {/* Summary Stat Cards */}
                    <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                        {LEAVE_CATEGORIES.map(cat => {
                            const val = roundVal(getCategoryValue(cat));
                            const clickable = val > 0;
                            return (
                                <Col xs={12} sm={8} md={6} lg={6} xl={6} key={cat.key} style={{ flex: "1 1 auto" }}>
                                    <div
                                        onClick={() => clickable && openDrilldown(cat)}
                                        style={{
                                            background: cat.bg, border: `1px solid ${cat.border}`,
                                            borderRadius: 10, padding: "8px 10px", textAlign: "center",
                                            cursor: clickable ? "pointer" : "default",
                                            transition: "all 0.18s ease",
                                        }}
                                        onMouseEnter={e => {
                                            if (clickable) {
                                                e.currentTarget.style.transform = "translateY(-2px)";
                                                e.currentTarget.style.boxShadow = `0 4px 12px ${cat.color}25`;
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (clickable) {
                                                e.currentTarget.style.transform = "none";
                                                e.currentTarget.style.boxShadow = "none";
                                            }
                                        }}
                                    >
                                        <div style={{ fontSize: 10.5, fontWeight: 800, color: cat.color, textTransform: "uppercase", marginBottom: 2 }}>
                                            {cat.abbr}
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 900, color: cat.color }}>
                                            {val}
                                        </div>
                                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {cat.label} {cat.unit ? `(${cat.unit})` : "Days"}
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                        <Col xs={12} sm={8} md={6} lg={6} xl={6} style={{ flex: "1 1 auto" }}>
                            <div style={{
                                background: "#f8fafc", border: "1px solid #e2e8f0",
                                borderRadius: 10, padding: "8px 10px", textAlign: "center",
                            }}>
                                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", marginBottom: 2 }}>
                                    TOTAL LEAVES
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                                    {roundVal(totalApprovedLeaveDays)}
                                </div>
                                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>Approved Days</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Donut Chart */}
                    {chartData.length > 0 && (
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={90}
                                    paddingAngle={3}
                                    label={({ name, value }) => `${name}: ${value}`}
                                    labelLine={false}
                                >
                                    {chartData.map((item, i) => (
                                        <Cell key={i} fill={item.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(v, name, props) => [
                                        `${v} days`,
                                        props.payload?.fullName || name,
                                    ]}
                                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                                />
                                <Legend
                                    formatter={(name, entry) => entry.payload?.fullName || name}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </>
            )}

            {/* Drilldown Employees Modal */}
            <Modal
                open={drillModal.open}
                onCancel={() => setDrillModal(prev => ({ ...prev, open: false }))}
                footer={null}
                width={620}
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                            width: 10, height: 10, borderRadius: "50%",
                            background: drillModal.color, display: "inline-block"
                        }} />
                        <span style={{ fontWeight: 800, color: "#0f172a" }}>{drillModal.title}</span>
                    </div>
                }
            >
                <div style={{ marginBottom: 12, marginTop: 8 }}>
                    <Search
                        placeholder="Search employee by name, code, or department..."
                        allowClear
                        value={drillSearch}
                        onChange={e => setDrillSearch(e.target.value)}
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                    />
                </div>

                <Table
                    loading={drillLoading}
                    dataSource={filteredDrillEmployees}
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 8, showTotal: (t) => `Total ${t} employees` }}
                    columns={[
                        {
                            title: "#", width: 45, align: "center",
                            render: (_, __, i) => <span style={{ color: "#94a3b8", fontWeight: 700 }}>{i + 1}</span>,
                        },
                        {
                            title: "Code", dataIndex: "emp_code", width: 90,
                            render: v => <Tag style={{ fontWeight: 800, borderRadius: 5 }}>{v || "-"}</Tag>,
                        },
                        {
                            title: "Employee", dataIndex: "name",
                            render: v => <span style={{ fontWeight: 800, color: "#0f172a" }}>{v}</span>,
                        },
                        {
                            title: "Department", dataIndex: "department",
                            render: v => <span style={{ color: "#64748b" }}>{v || "-"}</span>,
                        },
                    ]}
                />
            </Modal>
        </Card>
    );
}
